import { useEffect, useMemo, useRef, useState } from "react";
import {
  CalendarRange, Upload, RefreshCw, AlertTriangle, CheckCircle2, Plus, Trash2,
  Search, X, Users, ClipboardList, Info,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError, isSuperRole } from "../utils/auth";

const BULAN = [
  "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];
const HARI = ["M", "Sn", "Sl", "Rb", "Km", "Jm", "Sb"];
const LEBAR_KOLOM = 32; // px per hari — dipakai juga untuk menghitung posisi batang

// Warna batang: pucat supaya penanda bentrok (merah) tetap paling menonjol.
const WARNA = [
  ["#e0e7ff", "#3730a3"], ["#dbeafe", "#1e40af"], ["#cffafe", "#155e75"],
  ["#d1fae5", "#065f46"], ["#fef3c7", "#92400e"], ["#ffe4e6", "#9f1239"],
  ["#f3e8ff", "#6b21a8"], ["#fce7f3", "#9d174d"], ["#ecfccb", "#3f6212"],
  ["#ffedd5", "#9a3412"],
];

function warnaKegiatan(nama) {
  let h = 0;
  for (let i = 0; i < nama.length; i++) h = (h * 31 + nama.charCodeAt(i)) >>> 0;
  return WARNA[h % WARNA.length];
}

const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
function dariISO(s) {
  const [y, m, d] = String(s).split("-").map(Number);
  return new Date(y, m - 1, d);
}
function namaPendek(n) {
  const inti = String(n).split(",")[0].replace(/^H\.\s*/, "").trim();
  const kata = inti.split(/\s+/);
  return kata.length > 2 ? kata.slice(0, 2).join(" ") : inti;
}
function tanggalPendek(d) {
  return `${d.getDate()} ${BULAN[d.getMonth()].slice(0, 3)}`;
}

export default function KendaliPenugasan() {
  const { user } = useAuth();
  const bolehUbah = isSuperRole(user);
  const inputFileRef = useRef(null);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [pesan, setPesan] = useState(null);
  const [error, setError] = useState(null);

  const [bulan, setBulan] = useState(new Date().getMonth());
  const [cari, setCari] = useState("");
  const [hanyaBentrok, setHanyaBentrok] = useState(false);
  const [formTambah, setFormTambah] = useState(null); // {nama, kegiatan, mulai, selesai}
  const [menyimpan, setMenyimpan] = useState(false);

  function muat() {
    setLoading(true);
    setError(null);
    api
      .get("/penugasan-personil")
      .then(({ data }) => {
        setRows(data.data || []);
        setLoading(false);
      })
      .catch((e) => {
        setError(formatApiError(e?.response?.data?.detail) || "Gagal memuat data penugasan.");
        setLoading(false);
      });
  }

  useEffect(() => { muat(); }, []);

  function pilihFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    setPesan(null);
    setError(null);

    const fd = new FormData();
    fd.append("file", file);
    api
      .post("/penugasan-personil/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then(({ data }) => {
        setPesan(data.message || "Berhasil diunggah.");
        muat();
      })
      .catch((e) => setError(formatApiError(e?.response?.data?.detail) || "Gagal mengunggah file."))
      .finally(() => setUploading(false));
  }

  function simpanManual(e) {
    e.preventDefault();
    if (menyimpan || !formTambah) return;
    setMenyimpan(true);
    setError(null);
    api
      .post("/penugasan-personil", formTambah)
      .then(() => {
        setPesan("Penugasan ditambahkan.");
        setFormTambah(null);
        muat();
      })
      .catch((e) => setError(formatApiError(e?.response?.data?.detail) || "Gagal menyimpan penugasan."))
      .finally(() => setMenyimpan(false));
  }

  function hapus(id) {
    api
      .delete(`/penugasan-personil/${id}`)
      .then(() => { setPesan("Penugasan dihapus."); muat(); })
      .catch((e) => setError(formatApiError(e?.response?.data?.detail) || "Gagal menghapus penugasan."));
  }

  // ── Turunan data ────────────────────────────────────────────────────────
  const tugas = useMemo(
    () => rows
      .filter((r) => r.mulai && r.selesai)
      .map((r) => ({ ...r, m: dariISO(r.mulai), s: dariISO(r.selesai) })),
    [rows]
  );

  const personil = useMemo(() => {
    const set = new Map();
    tugas.forEach((t) => { if (!set.has(t.nama)) set.set(t.nama, namaPendek(t.nama)); });
    return [...set.entries()].map(([nama, pendek]) => ({ nama, pendek }));
  }, [tugas]);

  // nama -> { tglISO: [kegiatan] }, lalu hari yang berisi lebih dari satu kegiatan
  const { hariTerisi, hariBentrok } = useMemo(() => {
    const terisi = {};
    tugas.forEach((t) => {
      const map = (terisi[t.nama] ||= {});
      for (let d = new Date(t.m); d <= t.s; d.setDate(d.getDate() + 1)) {
        (map[iso(d)] ||= []).push(t.kegiatan);
      }
    });
    const bentrok = {};
    Object.entries(terisi).forEach(([nama, map]) => {
      bentrok[nama] = new Set(Object.keys(map).filter((k) => map[k].length > 1));
    });
    return { hariTerisi: terisi, hariBentrok: bentrok };
  }, [tugas]);

  const totalBentrok = useMemo(
    () => Object.values(hariBentrok).reduce((a, s) => a + s.size, 0),
    [hariBentrok]
  );

  const bentrokPerBulan = useMemo(() => {
    const arr = Array(12).fill(0);
    Object.values(hariBentrok).forEach((set) => {
      set.forEach((tgl) => { arr[dariISO(tgl).getMonth()] += 1; });
    });
    return arr;
  }, [hariBentrok]);

  const terpadat = useMemo(() => {
    const daftar = personil
      .map((p) => ({ nama: p.pendek, hari: Object.keys(hariTerisi[p.nama] || {}).length }))
      .sort((a, b) => b.hari - a.hari);
    return daftar[0] || null;
  }, [personil, hariTerisi]);

  const hariBulan = useMemo(() => {
    const th = new Date().getFullYear();
    const contoh = tugas[0];
    const tahun = contoh ? contoh.m.getFullYear() : th;
    const jml = new Date(tahun, bulan + 1, 0).getDate();
    return Array.from({ length: jml }, (_, i) => new Date(tahun, bulan, i + 1));
  }, [bulan, tugas]);

  // Baris matriks: personil + penugasan bulan ini, sudah disusun berlapis
  const barisMatriks = useMemo(() => {
    if (!hariBulan.length) return [];
    const awal = hariBulan[0];
    const akhir = hariBulan[hariBulan.length - 1];
    const q = cari.trim().toLowerCase();

    return personil
      .map((p) => {
        let punya = tugas.filter((t) => t.nama === p.nama && t.m <= akhir && t.s >= awal);
        if (q) {
          punya = punya.filter(
            (t) => p.nama.toLowerCase().includes(q) || t.kegiatan.toLowerCase().includes(q)
          );
        }
        const setBentrok = hariBentrok[p.nama] || new Set();
        const adaBentrok = [...setBentrok].some((tgl) => dariISO(tgl).getMonth() === bulan);

        // susun berlapis supaya batang tidak saling menimpa
        const lapis = [];
        [...punya].sort((a, b) => a.m - b.m || b.s - a.s).forEach((t) => {
          const idx = lapis.findIndex((l) => l.every((x) => t.m > x.s || t.s < x.m));
          if (idx < 0) lapis.push([t]); else lapis[idx].push(t);
        });

        return { ...p, punya, lapis, adaBentrok, setBentrok };
      })
      .filter((b) => {
        if (hanyaBentrok && !b.adaBentrok) return false;
        if (cari.trim() && !b.punya.length) return false;
        return true;
      });
  }, [personil, tugas, hariBulan, bulan, cari, hanyaBentrok, hariBentrok]);

  // Ringkasan bentrok bulan berjalan, tanggal berurutan digabung
  const daftarBentrok = useMemo(() => {
    const out = [];
    personil.forEach((p) => {
      const tgl = [...(hariBentrok[p.nama] || [])]
        .filter((t) => dariISO(t).getMonth() === bulan)
        .sort();
      let blok = [];
      tgl.forEach((t) => {
        const isi = (hariTerisi[p.nama][t] || []).join(" | ");
        const akhir = blok[blok.length - 1];
        const berurutan = akhir && (dariISO(t) - dariISO(akhir.sampai)) / 86400000 === 1;
        if (akhir && akhir.isi === isi && berurutan) akhir.sampai = t;
        else blok.push({ dari: t, sampai: t, isi, daftar: hariTerisi[p.nama][t] });
      });
      blok.forEach((b) => out.push({ nama: p.pendek, ...b }));
    });
    return out;
  }, [personil, hariBentrok, hariTerisi, bulan]);

  const manual = useMemo(() => rows.filter((r) => r.sumber === "MANUAL"), [rows]);

  // ── Tampilan ────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4" data-testid="page-kendali-penugasan">
      {/* Kop */}
      <div
        className="rounded-2xl px-6 py-5 text-white"
        style={{ background: "linear-gradient(135deg, #4338ca 0%, #6366f1 55%, #818cf8 100%)" }}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/20">
              <CalendarRange className="h-5 w-5" strokeWidth={2} />
            </div>
            <div>
              <h1 className="font-display text-lg font-bold leading-tight">Kendali Penugasan Personil</h1>
              <p className="mt-0.5 text-[11.5px] text-indigo-200">
                Peta jadwal seluruh personil — bentrok jadwal ditandai otomatis
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {bolehUbah && (
              <>
                <input
                  ref={inputFileRef}
                  type="file"
                  accept=".xlsx,.xlsm"
                  className="hidden"
                  onChange={pilihFile}
                  data-testid="penugasan-file-input"
                />
                <button
                  onClick={() => setFormTambah({ nama: personil[0]?.nama || "", kegiatan: "", mulai: "", selesai: "" })}
                  className="flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1.5 text-[11.5px] font-semibold transition-colors hover:bg-white/30"
                >
                  <Plus className="h-3.5 w-3.5" strokeWidth={2.2} />
                  Tambah
                </button>
                <button
                  onClick={() => inputFileRef.current?.click()}
                  disabled={uploading}
                  title="Unggah file PPBR (sheet REALISASI PENUGASAN)"
                  className="flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1.5 text-[11.5px] font-semibold transition-colors hover:bg-white/30 disabled:opacity-50"
                >
                  {uploading
                    ? <RefreshCw className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
                    : <Upload className="h-3.5 w-3.5" strokeWidth={2.2} />}
                  Unggah Excel
                </button>
              </>
            )}
            <button
              onClick={muat}
              title="Muat ulang"
              className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 transition-colors hover:bg-white/30"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} strokeWidth={2} />
            </button>
          </div>
        </div>
      </div>

      {/* Pesan */}
      {pesan && (
        <div className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-700">
          <CheckCircle2 className="mt-px h-4 w-4 flex-shrink-0" strokeWidth={2} />
          <span className="break-words">{pesan}</span>
        </div>
      )}
      {error && (
        <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">
          <AlertTriangle className="mt-px h-4 w-4 flex-shrink-0" strokeWidth={2} />
          <span className="break-words">{error}</span>
        </div>
      )}

      {/* Kartu angka */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kotak icon={Users} label="Personil" nilai={personil.length} />
        <Kotak icon={ClipboardList} label="Penugasan" nilai={tugas.length} />
        <Kotak
          icon={AlertTriangle}
          label="Hari bentrok"
          nilai={totalBentrok}
          awas
        />
        <Kotak
          icon={CalendarRange}
          label={terpadat ? `Terpadat · ${terpadat.hari} hari` : "Terpadat"}
          nilai={terpadat ? terpadat.nama : "—"}
          kecil
        />
      </div>

      {/* Peta penugasan */}
      <div className="rounded-2xl border border-indigo-100/70 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
          <div>
            <div className="text-[13px] font-bold text-slate-800">Peta Penugasan</div>
            <div className="mt-0.5 text-[11px] text-slate-500">
              Tiap batang satu penugasan. Kolom merah muda menandai hari dengan lebih dari satu kegiatan.
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={cari}
                onChange={(e) => setCari(e.target.value)}
                placeholder="Cari nama atau kegiatan..."
                className="w-52 rounded-lg border border-slate-200 py-1.5 pl-8 pr-3 text-[11.5px] text-slate-700 placeholder-slate-400 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              />
            </div>
            <label className="flex cursor-pointer select-none items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[11.5px] text-slate-600">
              <input
                type="checkbox"
                checked={hanyaBentrok}
                onChange={(e) => setHanyaBentrok(e.target.checked)}
                className="accent-indigo-600"
              />
              Hanya yang bentrok
            </label>
          </div>
        </div>

        {/* Tab bulan */}
        <div className="flex flex-wrap gap-1.5 border-b border-slate-100 px-4 py-2.5">
          {BULAN.map((nm, i) => (
            <button
              key={nm}
              onClick={() => setBulan(i)}
              className={`rounded-lg border px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                i === bulan
                  ? "border-indigo-600 bg-indigo-600 text-white"
                  : "border-slate-200 bg-white text-slate-500 hover:border-indigo-300 hover:text-indigo-600"
              }`}
            >
              {nm.slice(0, 3)}
              {bentrokPerBulan[i] > 0 && (
                <span
                  className={`ml-1.5 inline-block min-w-[16px] rounded-full px-1 text-[9.5px] font-bold leading-[15px] ${
                    i === bulan ? "bg-white text-rose-600" : "bg-rose-500 text-white"
                  }`}
                >
                  {bentrokPerBulan[i]}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Matriks */}
        {loading ? (
          <div className="flex flex-col items-center gap-2 py-14 text-indigo-400">
            <RefreshCw className="h-5 w-5 animate-spin" />
            <span className="text-[12px]">Memuat data…</span>
          </div>
        ) : tugas.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-4 py-14 text-center">
            <CalendarRange className="h-8 w-8 text-indigo-300" strokeWidth={1.5} />
            <div className="text-[12.5px] font-semibold text-slate-600">Belum ada data penugasan</div>
            <div className="max-w-[280px] text-[11px] text-slate-400">
              {bolehUbah
                ? "Unggah file PPBR lewat tombol di pojok kanan atas, atau tambahkan penugasan satu per satu."
                : "Hubungi Admin/IRBAN untuk mengunggah data penugasan."}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-max">
              {/* Kepala tanggal */}
              <div className="flex border-b border-slate-200">
                <div className="sticky left-0 z-10 w-[172px] min-w-[172px] border-r border-slate-200 bg-white px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  Personil
                </div>
                <div className="flex">
                  {hariBulan.map((d) => {
                    const pekan = d.getDay() === 0 || d.getDay() === 6;
                    return (
                      <div
                        key={d.getDate()}
                        style={{ width: LEBAR_KOLOM, minWidth: LEBAR_KOLOM }}
                        className={`border-r border-slate-50 py-1.5 text-center text-[10px] ${
                          pekan ? "bg-slate-50 text-slate-400" : "text-slate-500"
                        }`}
                      >
                        {d.getDate()}
                        <span className="block text-[8.5px] uppercase text-slate-400">{HARI[d.getDay()]}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Baris personil */}
              {barisMatriks.map((b) => (
                <div key={b.nama} className="flex border-b border-slate-50 last:border-b-0">
                  <div className="sticky left-0 z-10 flex w-[172px] min-w-[172px] items-center gap-2 border-r border-slate-200 bg-white px-3 py-2">
                    <span
                      className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${
                        b.adaBentrok ? "bg-rose-500" : "bg-slate-300"
                      }`}
                    />
                    <span className="leading-tight">
                      <span className="block text-[11.5px] font-semibold text-slate-800">{b.pendek}</span>
                      <span className="block text-[9.5px] text-slate-400">{b.punya.length} penugasan</span>
                    </span>
                  </div>

                  <div className="relative flex-1">
                    {/* latar kolom */}
                    <div className="pointer-events-none absolute inset-0 flex">
                      {hariBulan.map((d) => {
                        const pekan = d.getDay() === 0 || d.getDay() === 6;
                        const bentrok = b.setBentrok.has(iso(d));
                        return (
                          <span
                            key={d.getDate()}
                            style={{ width: LEBAR_KOLOM, minWidth: LEBAR_KOLOM }}
                            className={`border-r border-slate-50 ${
                              bentrok ? "bg-rose-50" : pekan ? "bg-slate-50" : ""
                            }`}
                          />
                        );
                      })}
                    </div>

                    {b.lapis.length === 0 ? (
                      <div className="h-[30px]" />
                    ) : (
                      b.lapis.map((baris, li) => (
                        <div key={li} className="relative my-[3px] h-6">
                          {baris.map((t) => {
                            const jml = hariBulan.length;
                            const mulai = t.m < hariBulan[0] ? 1 : t.m.getDate();
                            const habis = t.s > hariBulan[jml - 1] ? jml : t.s.getDate();
                            const kiri = (mulai - 1) * LEBAR_KOLOM + 2;
                            const lebar = (habis - mulai + 1) * LEBAR_KOLOM - 4;
                            const [bg, fg] = warnaKegiatan(t.kegiatan);
                            const tabrak = [...b.setBentrok].some((tgl) => {
                              const d = dariISO(tgl);
                              return d >= t.m && d <= t.s;
                            });
                            return (
                              <div
                                key={t.id}
                                title={`${t.kegiatan}\n${tanggalPendek(t.m)} – ${tanggalPendek(t.s)}${
                                  tabrak ? "\n⚠ Bentrok dengan kegiatan lain" : ""
                                }${t.sumber === "MANUAL" ? "\n(input manual)" : ""}`}
                                style={{
                                  left: kiri, width: lebar, background: bg, color: fg,
                                  boxShadow: tabrak ? "inset 0 0 0 1.5px #e11d48" : undefined,
                                }}
                                className="absolute top-0.5 flex h-5 items-center overflow-hidden text-ellipsis whitespace-nowrap rounded-md border border-slate-900/5 px-1.5 text-[10.5px] font-semibold"
                              >
                                {lebar > 52 ? t.kegiatan : ""}
                              </div>
                            );
                          })}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))}

              {barisMatriks.length === 0 && (
                <div className="px-4 py-10 text-center text-[12px] italic text-slate-400">
                  Tidak ada penugasan yang cocok pada {BULAN[bulan]}.
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Daftar bentrok */}
      <div className="rounded-2xl border border-indigo-100/70 bg-white">
        <div className="border-b border-slate-100 px-4 py-3">
          <div className="text-[13px] font-bold text-slate-800">Bentrok Terdeteksi</div>
          <div className="mt-0.5 text-[11px] text-slate-500">
            {daftarBentrok.length
              ? `${daftarBentrok.length} kejadian pada ${BULAN[bulan]} — satu orang, lebih dari satu kegiatan di hari yang sama.`
              : `Tidak ada bentrok pada ${BULAN[bulan]}.`}
          </div>
        </div>
        <div className="px-4 py-1">
          {daftarBentrok.length === 0 ? (
            <div className="py-8 text-center text-[12px] text-slate-400">
              Bersih — tidak ada personil yang terjadwal ganda bulan ini.
            </div>
          ) : (
            daftarBentrok.map((b, i) => {
              const d1 = dariISO(b.dari);
              const d2 = dariISO(b.sampai);
              const rentang = b.dari === b.sampai ? tanggalPendek(d1) : `${d1.getDate()}–${tanggalPendek(d2)}`;
              return (
                <div
                  key={`${b.nama}-${b.dari}-${i}`}
                  className="flex items-start gap-3 border-b border-dashed border-slate-100 py-2.5 last:border-b-0"
                >
                  <div className="min-w-[112px] rounded-lg border border-rose-200 bg-rose-50 px-2 py-1 text-center text-[10.5px] font-bold text-rose-600">
                    {rentang}
                  </div>
                  <div className="flex-1">
                    <div className="text-[12px] font-bold text-slate-800">{b.nama}</div>
                    <div className="mt-0.5 text-[11.5px] leading-relaxed text-slate-500">
                      {b.daftar.map((x, j) => (
                        <span key={j}>
                          {j > 0 && <span className="text-slate-300"> · </span>}
                          <b className="font-semibold text-slate-700">{x}</b>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Penugasan manual */}
      {bolehUbah && manual.length > 0 && (
        <div className="rounded-2xl border border-indigo-100/70 bg-white">
          <div className="border-b border-slate-100 px-4 py-3">
            <div className="text-[13px] font-bold text-slate-800">Penugasan Tambahan</div>
            <div className="mt-0.5 text-[11px] text-slate-500">
              Diinput langsung di aplikasi — tidak ikut terhapus saat file Excel diunggah ulang.
            </div>
          </div>
          <div className="px-4 py-1">
            {manual.map((m) => (
              <div
                key={m.id}
                className="flex items-center gap-3 border-b border-dashed border-slate-100 py-2.5 last:border-b-0"
              >
                <div className="flex-1">
                  <div className="text-[12px] font-semibold text-slate-800">{m.kegiatan}</div>
                  <div className="text-[11px] text-slate-500">
                    {namaPendek(m.nama)} · {m.mulai} → {m.selesai}
                  </div>
                </div>
                <button
                  onClick={() => hapus(m.id)}
                  title="Hapus penugasan"
                  className="rounded-lg border border-slate-200 p-1.5 text-slate-400 transition-colors hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600"
                >
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={2} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal tambah */}
      {formTambah && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <form
            onSubmit={simpanManual}
            className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl"
          >
            <div
              className="flex items-center justify-between px-4 py-3 text-white"
              style={{ background: "linear-gradient(135deg, #4338ca 0%, #6366f1 100%)" }}
            >
              <span className="text-[13px] font-bold">Tambah Penugasan</span>
              <button
                type="button"
                onClick={() => setFormTambah(null)}
                className="rounded-full p-1 text-white/80 hover:bg-white/20 hover:text-white"
              >
                <X className="h-4 w-4" strokeWidth={2.2} />
              </button>
            </div>

            <div className="space-y-3 px-4 py-4">
              <Bidang label="Personil">
                <input
                  list="daftar-personil"
                  required
                  value={formTambah.nama}
                  onChange={(e) => setFormTambah({ ...formTambah, nama: e.target.value })}
                  placeholder="Ketik atau pilih nama"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[12.5px] focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                />
                <datalist id="daftar-personil">
                  {personil.map((p) => <option key={p.nama} value={p.nama} />)}
                </datalist>
              </Bidang>

              <Bidang label="Kegiatan">
                <input
                  required
                  value={formTambah.kegiatan}
                  onChange={(e) => setFormTambah({ ...formTambah, kegiatan: e.target.value })}
                  placeholder="Nama kegiatan / penugasan"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[12.5px] focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                />
              </Bidang>

              <div className="grid grid-cols-2 gap-3">
                <Bidang label="Mulai">
                  <input
                    type="date"
                    required
                    value={formTambah.mulai}
                    onChange={(e) => setFormTambah({ ...formTambah, mulai: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[12.5px] focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                  />
                </Bidang>
                <Bidang label="Selesai">
                  <input
                    type="date"
                    required
                    value={formTambah.selesai}
                    onChange={(e) => setFormTambah({ ...formTambah, selesai: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[12.5px] focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                  />
                </Bidang>
              </div>

              <div className="flex items-start gap-1.5 text-[11px] leading-relaxed text-slate-500">
                <Info className="mt-px h-3.5 w-3.5 flex-shrink-0 text-sky-500" strokeWidth={2} />
                <span>
                  Penugasan ini disimpan terpisah dari data Excel, jadi tetap aman saat file PPBR
                  diunggah ulang. Bentrok akan tetap ditandai, tidak menghalangi penyimpanan.
                </span>
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-100 bg-slate-50 px-4 py-2.5">
              <button
                type="button"
                onClick={() => setFormTambah(null)}
                className="rounded-lg px-3 py-1.5 text-[12px] font-semibold text-slate-500 hover:bg-slate-200 hover:text-slate-700"
              >
                Batal
              </button>
              <button
                type="submit"
                disabled={menyimpan}
                className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-[12px] font-bold text-white hover:bg-indigo-700 disabled:opacity-60"
              >
                {menyimpan && <RefreshCw className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />}
                Simpan
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Kotak({ icon: Icon, label, nilai, awas, kecil }) {
  return (
    <div
      className={`rounded-2xl border bg-white px-4 py-3 ${
        awas ? "border-rose-200" : "border-indigo-100/70"
      }`}
      style={awas ? { background: "linear-gradient(135deg, #fff1f2 0%, #ffffff 70%)" } : undefined}
    >
      <div className="flex items-center gap-2">
        <Icon className={`h-3.5 w-3.5 ${awas ? "text-rose-500" : "text-indigo-400"}`} strokeWidth={2} />
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      </div>
      <div
        className={`mt-1 font-bold leading-tight ${kecil ? "text-[15px]" : "text-2xl"} ${
          awas ? "text-rose-600" : "text-slate-800"
        }`}
      >
        {nilai}
      </div>
    </div>
  );
}

function Bidang({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </span>
      {children}
    </label>
  );
}
